import { D } from "@mobily/ts-belt";
import PropTypes from "prop-types";
import * as React from "react";
import { useHistory } from "react-router-dom";
import pc from "../../ShopV2/components/ProductCard.module.css";
import ProductCard from "../../ShopV2/components/ProductCard";
import adaptProductForCard from "../productAdapter";
import { useProducts } from "../utils";
import s from "./ProductGrid.module.css";
import { Skeleton } from "../../../themeV2";

const SpinnerIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" opacity="0.25" />
    <path d="M12 2a10 10 0 0 1 10 10" />
  </svg>
);

const SKELETON_COUNT = 6;

function SkeletonCard() {
  return (
    <div
      className={pc.card}
      style={{ pointerEvents: "none" }}
      aria-hidden="true"
    >
      <div className={pc.cardImg}>
        <Skeleton
          variant="rect"
          style={{
            width: "100%",
            height: "100%",
            borderRadius: "var(--shape-md) var(--shape-md) 0 0",
          }}
        />
      </div>
      <div className={pc.body}>
        <Skeleton variant="text" style={{ width: "85%", marginBottom: 8 }} />
        <Skeleton variant="text" style={{ width: "60%", marginBottom: 4 }} />
        <Skeleton variant="text" style={{ width: "45%" }} />
        <div className={pc.footer}>
          <Skeleton variant="text" size="lg" style={{ width: 48 }} />
          <Skeleton
            variant="rect"
            style={{ width: 72, height: 36, borderRadius: "var(--shape-xl)" }}
          />
        </div>
      </div>
    </div>
  );
}

const formatTotal = (total) => (total >= 1000 ? "999+" : String(total));

const resultsTextFor = ({ filter, pagination }) => {
  if (filter.name) {
    const total = formatTotal(pagination.totalResults);
    const resultText = pagination.totalResults === 1 ? "result" : "results";

    return `Showing ${total} ${resultText} for "${filter.name}"`;
  }

  const total = formatTotal(pagination.totalResults);
  const productText = pagination.totalResults === 1 ? "product" : "products";

  return `${total} ${productText}`;
};

export default function ProductGrid({ filter, setFilter }) {
  const history = useHistory();
  const { products, pagination, isLoading, isValidating } = useProducts(filter);
  const [cursorStack, setCursorStack] = React.useState([]);

  React.useEffect(() => {
    document.getElementById("root").scrollIntoView({ behavior: "smooth" });
  }, [filter.cursor]);

  const handleProductClick = React.useCallback(
    (cardProduct) => {
      history.push(`/${cardProduct.route}`, {
        breadcrumb: { to: "/products", label: "Browse Products" },
      });
    },
    [history],
  );

  const filterIdentity = React.useMemo(
    () =>
      JSON.stringify({
        order: filter.order,
        franchise: filter.franchise,
        type: filter.type,
        signedType: filter.signedType,
        minPrice: filter.minPrice,
        maxPrice: filter.maxPrice,
        name: filter.name,
      }),
    [
      filter.order,
      filter.franchise,
      filter.type,
      filter.signedType,
      filter.minPrice,
      filter.maxPrice,
      filter.name,
    ],
  );

  React.useEffect(() => {
    setCursorStack([]);
  }, [filterIdentity]);

  const handlePrevious = React.useCallback(() => {
    const previousCursor = cursorStack.at(-1) ?? "";
    setCursorStack((stack) => stack.slice(0, -1));
    setFilter(D.merge({ cursor: previousCursor }));
  }, [cursorStack, setFilter]);

  const handleNext = React.useCallback(() => {
    if (!pagination.nextCursor) return;
    setCursorStack((stack) => [...stack, filter.cursor || ""]);
    setFilter(D.merge({ cursor: pagination.nextCursor }));
  }, [filter.cursor, pagination.nextCursor, setFilter]);

  const gridClass =
    isValidating && !isLoading ? `${s.grid} ${s.gridLoading}` : s.grid;

  return (
    <div>
      <div className={s.header}>
        <h2 className={s.title}>
          Products
          {isValidating && !isLoading && <SpinnerIcon />}
        </h2>
        <span className={s.resultsText} aria-live="polite">
          {isLoading ? (
            <span aria-hidden="true">
              <Skeleton
                variant="text"
                as="span"
                style={{ width: 80, display: "inline-block" }}
              />
            </span>
          ) : (
            resultsTextFor({ filter, pagination })
          )}
        </span>
      </div>

      {isLoading ? (
        <div className={s.grid}>
          {Array.from({ length: SKELETON_COUNT }, (_, i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      ) : (
        <>
          <div className={gridClass}>
            {products.map((product) => {
              const cardProduct = adaptProductForCard(product);
              return (
                <ProductCard
                  key={product.productId}
                  product={cardProduct}
                  onClick={handleProductClick}
                />
              );
            })}
          </div>

          {products.length === 0 && (
            <p className={s.empty}>No products found</p>
          )}

          {products.length > 0 && (
            <nav className={s.pagination} aria-label="Product pagination">
              <button
                type="button"
                className={s.paginationBtn}
                disabled={cursorStack.length === 0}
                onClick={handlePrevious}
              >
                Previous
              </button>
              <button
                type="button"
                className={s.paginationBtn}
                disabled={!pagination?.hasNext}
                onClick={handleNext}
              >
                Next
              </button>
            </nav>
          )}
        </>
      )}
    </div>
  );
}

ProductGrid.propTypes = {
  filter: PropTypes.shape({
    name: PropTypes.string,
    cursor: PropTypes.string,
    signedType: PropTypes.object,
  }).isRequired,
  setFilter: PropTypes.func.isRequired,
};
